import React, { useState, useEffect, useCallback } from 'react';
import { View, Text, StyleSheet, ScrollView, Pressable, TextInput, Platform, RefreshControl, ActivityIndicator, Switch } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useNavigation, useRoute } from '@react-navigation/native';
import { NativeStackNavigationProp, NativeStackScreenProps } from '@react-navigation/native-stack';
import { MaterialIcons, Ionicons } from '@expo/vector-icons';
import type { RootStackParamList } from '../navigation/stackTypes';
import { isSuccessful } from '../services/api-requests';
import Header from '../components/Header';
import LoadingButton from '../components/LoadingButton';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import { getPricingGroupDetail, updatePricingGroup, addPricingGroupMember, revokePricingGroupMember, restorePricingGroupMember } from '../services/pricing-group-service';
import type { PricingGroup, PricingGroupMember, DiscountType, GroupScope } from '../models/pricing-group-model';
import { formatDiscountLabel, DISCOUNT_BADGE_COLORS } from '../models/pricing-group-model';
import { showThemedAlert } from '../components/common/ThemedAlert';

type TabKey = 'details' | 'members' | 'chargers';

const TABS: { key: TabKey; label: string }[] = [
    { key: 'details', label: 'Details' },
    { key: 'members', label: 'Members' },
    { key: 'chargers', label: 'Chargers' },
];

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const PricingGroupDetail = () => {
    const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
    const route = useRoute<NativeStackScreenProps<RootStackParamList, 'PricingGroupDetail'>['route']>();
    const { groupId } = route.params;

    const myChargers = useSelector((state: RootState) => state.chargers.myChargers);

    const [group, setGroup] = useState<PricingGroup | null>(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [activeTab, setActiveTab] = useState<TabKey>('details');

    // Details tab state
    const [editingName, setEditingName] = useState(false);
    const [nameValue, setNameValue] = useState('');
    const [savingName, setSavingName] = useState(false);
    const [togglingActive, setTogglingActive] = useState(false);
    const [editingDiscount, setEditingDiscount] = useState(false);
    const [discountRaw, setDiscountRaw] = useState('');
    const [savingDiscount, setSavingDiscount] = useState(false);

    // Members tab state
    const [newMemberEmail, setNewMemberEmail] = useState('');
    const [addingMember, setAddingMember] = useState(false);
    const [memberActionId, setMemberActionId] = useState<string | null>(null);
    const [showRevoked, setShowRevoked] = useState(false);

    // Chargers tab state
    const [selectedChargerIds, setSelectedChargerIds] = useState<string[]>([]);
    const [savingChargers, setSavingChargers] = useState(false);
    const [chargersDirty, setChargersDirty] = useState(false);

    const fetchGroup = useCallback(async () => {
        try {
            const response = await getPricingGroupDetail(groupId);
            if (isSuccessful(response.status) && response.payload) {
                const fetched = response.payload;
                setGroup(fetched);
                setNameValue(fetched.name);
                setDiscountRaw(String(Math.round(fetched.discount_value)));
                if (fetched.scope === 'specific_chargers' && fetched.chargers) {
                    setSelectedChargerIds(fetched.chargers.map(c => c.charger_id));
                }
            } else {
                showThemedAlert('Error', 'Failed to load pricing group details.');
            }
        } catch {
            showThemedAlert('Error', 'An unexpected error occurred.');
        }
    }, [groupId]);

    useEffect(() => {
        let mounted = true;
        (async () => {
            setLoading(true);
            await fetchGroup();
            if (mounted) setLoading(false);
        })();
        return () => { mounted = false; };
    }, [fetchGroup]);

    const onRefresh = useCallback(async () => {
        setRefreshing(true);
        await fetchGroup();
        setRefreshing(false);
    }, [fetchGroup]);

    // ─── Details ────────────────────────────────────────────

    const handleSaveName = useCallback(async () => {
        if (!group || !nameValue.trim() || nameValue.trim() === group.name) {
            setEditingName(false);
            return;
        }
        setSavingName(true);
        try {
            const response = await updatePricingGroup(groupId, { name: nameValue.trim() });
            if (isSuccessful(response.status)) {
                setGroup(prev => prev ? { ...prev, name: nameValue.trim() } : prev);
                setEditingName(false);
            } else {
                showThemedAlert('Error', 'Failed to update group name.');
            }
        } catch {
            showThemedAlert('Error', 'An unexpected error occurred.');
        } finally {
            setSavingName(false);
        }
    }, [group, nameValue, groupId]);

    const handleDiscountChange = useCallback((text: string) => {
        const cleaned = text.replace(/[^0-9]/g, '');
        const num = parseInt(cleaned, 10);
        if (cleaned === '' || (num >= 0 && num <= 100)) {
            setDiscountRaw(cleaned);
        }
    }, []);

    const handleSaveDiscount = useCallback(async () => {
        if (!group) return;
        const parsed = parseInt(discountRaw, 10);
        if (isNaN(parsed) || parsed < 1 || parsed > 100) {
            showThemedAlert('Invalid Value', 'Please enter a whole number between 1 and 100.');
            return;
        }
        if (parsed === group.discount_value) {
            setEditingDiscount(false);
            return;
        }
        setSavingDiscount(true);
        try {
            const response = await updatePricingGroup(groupId, { discount_value: parsed });
            if (isSuccessful(response.status)) {
                setGroup(prev => prev ? { ...prev, discount_value: parsed } : prev);
                setEditingDiscount(false);
            } else {
                showThemedAlert('Error', 'Failed to update discount value.');
            }
        } catch {
            showThemedAlert('Error', 'An unexpected error occurred.');
        } finally {
            setSavingDiscount(false);
        }
    }, [group, discountRaw, groupId]);

    const handleToggleActive = useCallback(() => {
        if (!group) return;
        const action = group.is_active ? 'Deactivate' : 'Activate';
        showThemedAlert(
            `${action} Group`,
            `Are you sure you want to ${action.toLowerCase()} "${group.name}"?`,
            [
                { text: 'Cancel', style: 'cancel' },
                {
                    text: action,
                    style: group.is_active ? 'destructive' : 'default',
                    onPress: async () => {
                        setTogglingActive(true);
                        try {
                            const response = await updatePricingGroup(groupId, { is_active: !group.is_active });
                            if (isSuccessful(response.status)) {
                                setGroup(prev => prev ? { ...prev, is_active: !prev.is_active } : prev);
                            } else {
                                showThemedAlert('Error', `Failed to ${action.toLowerCase()} group.`);
                            }
                        } catch {
                            showThemedAlert('Error', 'An unexpected error occurred.');
                        } finally {
                            setTogglingActive(false);
                        }
                    },
                },
            ],
        );
    }, [group, groupId]);

    // ─── Members ────────────────────────────────────────────

    const handleAddMember = useCallback(async () => {
        const email = newMemberEmail.trim().toLowerCase();
        if (!EMAIL_REGEX.test(email)) {
            showThemedAlert('Invalid Email', 'Please enter a valid email address.');
            return;
        }
        setAddingMember(true);
        try {
            const response = await addPricingGroupMember(groupId, email);
            console.log('[addPricingGroupMember] full response:', JSON.stringify(response, null, 2));
            if (isSuccessful(response.status)) {
                setNewMemberEmail('');
                await fetchGroup();
            } else {
                const msg = (response as any).errorPayload?.message
                    || (response as any).payload?.message
                    || 'Failed to add member.';
                showThemedAlert('Error', msg);
            }
        } catch {
            showThemedAlert('Error', 'An unexpected error occurred.');
        } finally {
            setAddingMember(false);
        }
    }, [newMemberEmail, groupId, fetchGroup]);

    const handleRevokeMember = useCallback((member: PricingGroupMember) => {
        showThemedAlert(
            'Revoke Member',
            `Revoke access for ${member.user_email}?`,
            [
                { text: 'Cancel', style: 'cancel' },
                {
                    text: 'Revoke',
                    style: 'destructive',
                    onPress: async () => {
                        setMemberActionId(member.id);
                        try {
                            const response = await revokePricingGroupMember(groupId, member.id);
                            if (isSuccessful(response.status)) {
                                await fetchGroup();
                            } else {
                                showThemedAlert('Error', 'Failed to revoke member.');
                            }
                        } catch {
                            showThemedAlert('Error', 'An unexpected error occurred.');
                        } finally {
                            setMemberActionId(null);
                        }
                    },
                },
            ],
        );
    }, [groupId, fetchGroup]);

    const handleRestoreMember = useCallback(async (member: PricingGroupMember) => {
        setMemberActionId(member.id);
        try {
            const response = await restorePricingGroupMember(groupId, member.id);
            if (isSuccessful(response.status)) {
                await fetchGroup();
            } else {
                showThemedAlert('Error', 'Failed to restore member.');
            }
        } catch {
            showThemedAlert('Error', 'An unexpected error occurred.');
        } finally {
            setMemberActionId(null);
        }
    }, [groupId, fetchGroup]);

    // ─── Chargers ───────────────────────────────────────────

    const toggleChargerSelection = useCallback((chargerId: string) => {
        setSelectedChargerIds(prev => {
            const next = prev.includes(chargerId)
                ? prev.filter(id => id !== chargerId)
                : [...prev, chargerId];
            return next;
        });
        setChargersDirty(true);
    }, []);

    const handleSaveChargers = useCallback(async () => {
        if (!group) return;
        setSavingChargers(true);
        try {
            const response = await updatePricingGroup(groupId, { charger_ids: selectedChargerIds });
            if (isSuccessful(response.status)) {
                setChargersDirty(false);
                await fetchGroup();
            } else {
                showThemedAlert('Error', 'Failed to update charger assignments.');
            }
        } catch {
            showThemedAlert('Error', 'An unexpected error occurred.');
        } finally {
            setSavingChargers(false);
        }
    }, [group, groupId, selectedChargerIds, fetchGroup]);

    // ─── Renderers ──────────────────────────────────────────

    const renderTabs = () => (
        <View style={styles.tabRow}>
            {TABS.map(tab => {
                const isActive = activeTab === tab.key;
                return (
                    <Pressable
                        key={tab.key}
                        onPress={() => setActiveTab(tab.key)}
                        style={[styles.tab, isActive && styles.tabActive]}
                    >
                        <Text style={[styles.tabText, isActive && styles.tabTextActive]}>
                            {tab.label}
                        </Text>
                    </Pressable>
                );
            })}
        </View>
    );

    const renderDetailsTab = () => {
        if (!group) return null;
        const badgeColor = DISCOUNT_BADGE_COLORS[group.discount_type];
        return (
            <View style={styles.tabContent}>
                {/* Group Name */}
                <View style={styles.card}>
                    <Text style={styles.cardLabel}>Group Name</Text>
                    {editingName ? (
                        <View style={styles.inlineEditRow}>
                            <TextInput
                                style={styles.inlineInput}
                                value={nameValue}
                                onChangeText={setNameValue}
                                autoFocus
                                placeholderTextColor="#8D8E90"
                                maxLength={60}
                            />
                            <Pressable
                                onPress={handleSaveName}
                                disabled={savingName}
                                style={({ pressed }) => [styles.inlineBtn, { opacity: pressed ? 0.6 : 1 }]}
                            >
                                {savingName
                                    ? <ActivityIndicator size="small" color="#40BFB4" />
                                    : <MaterialIcons name="check" size={22} color="#40BFB4" />}
                            </Pressable>
                            <Pressable
                                onPress={() => { setEditingName(false); setNameValue(group.name); }}
                                style={({ pressed }) => [styles.inlineBtn, { opacity: pressed ? 0.6 : 1 }]}
                            >
                                <MaterialIcons name="close" size={22} color="#8D8E90" />
                            </Pressable>
                        </View>
                    ) : (
                        <Pressable
                            onPress={() => setEditingName(true)}
                            style={({ pressed }) => [styles.nameRow, { opacity: pressed ? 0.7 : 1 }]}
                        >
                            <Text style={styles.nameText}>{group.name}</Text>
                            <MaterialIcons name="edit" size={18} color="#8D8E90" />
                        </Pressable>
                    )}
                </View>

                {/* Discount */}
                <View style={styles.card}>
                    <Text style={styles.cardLabel}>Discount</Text>
                    <View style={styles.discountRow}>
                        <View style={[styles.discountBadge, { backgroundColor: badgeColor }]}>
                            <Text style={styles.discountBadgeText}>
                                {formatDiscountLabel(group.discount_type, group.discount_value)}
                            </Text>
                        </View>
                        <Text style={styles.discountTypeText}>
                            {group.discount_type === 'free' ? 'Free Charging' :
                                group.discount_type === 'percentage' ? 'Percentage Discount' :
                                    'Flat Rate Override'}
                        </Text>
                    </View>
                    {group.discount_type === 'percentage' && (
                        editingDiscount ? (
                            <View style={styles.percentEditRow}>
                                <View style={styles.percentInputWrap}>
                                    <TextInput
                                        style={styles.percentInput}
                                        value={discountRaw}
                                        onChangeText={handleDiscountChange}
                                        keyboardType="number-pad"
                                        maxLength={3}
                                        autoFocus
                                        placeholderTextColor="#8D8E90"
                                        placeholder="0"
                                    />
                                    <Text style={styles.percentSign}>%</Text>
                                </View>
                                <Pressable
                                    onPress={handleSaveDiscount}
                                    disabled={savingDiscount}
                                    style={({ pressed }) => [styles.inlineBtn, { opacity: pressed ? 0.6 : 1 }]}
                                >
                                    {savingDiscount
                                        ? <ActivityIndicator size="small" color="#40BFB4" />
                                        : <MaterialIcons name="check" size={22} color="#40BFB4" />}
                                </Pressable>
                                <Pressable
                                    onPress={() => { setEditingDiscount(false); setDiscountRaw(String(Math.round(group.discount_value))); }}
                                    style={({ pressed }) => [styles.inlineBtn, { opacity: pressed ? 0.6 : 1 }]}
                                >
                                    <MaterialIcons name="close" size={22} color="#8D8E90" />
                                </Pressable>
                            </View>
                        ) : (
                            <Pressable
                                onPress={() => { setDiscountRaw(String(Math.round(group.discount_value))); setEditingDiscount(true); }}
                                style={({ pressed }) => [styles.percentTapRow, { opacity: pressed ? 0.7 : 1 }]}
                            >
                                <Text style={styles.percentTapLabel}>Tap to edit percentage</Text>
                                <MaterialIcons name="edit" size={16} color="#8D8E90" />
                            </Pressable>
                        )
                    )}
                </View>

                {/* Scope */}
                <View style={styles.card}>
                    <Text style={styles.cardLabel}>Scope</Text>
                    <View style={styles.scopeRow}>
                        <Ionicons
                            name={group.scope === 'all_host_chargers' ? 'globe-outline' : 'list-outline'}
                            size={20}
                            color="#40BFB4"
                        />
                        <Text style={styles.scopeText}>
                            {group.scope === 'all_host_chargers' ? 'All Host Chargers' : 'Specific Chargers'}
                        </Text>
                    </View>
                </View>

                {/* Status */}
                <View style={styles.card}>
                    <Text style={styles.cardLabel}>Status</Text>
                    <View style={styles.statusRow}>
                        <View style={[styles.statusDot, { backgroundColor: group.is_active ? '#10B981' : '#EF4444' }]} />
                        <Text style={styles.statusText}>{group.is_active ? 'Active' : 'Inactive'}</Text>
                    </View>
                    <LoadingButton
                        onPress={handleToggleActive}
                        backgroundColor={group.is_active ? '#EF4444' : '#10B981'}
                        isLoading={togglingActive}
                        style={styles.toggleBtn}
                        textStyle={styles.toggleBtnText}
                    >
                        {group.is_active ? 'Deactivate Group' : 'Activate Group'}
                    </LoadingButton>
                </View>

                {/* Meta */}
                <View style={styles.card}>
                    <Text style={styles.cardLabel}>Info</Text>
                    <View style={styles.metaRow}>
                        <Text style={styles.metaLabel}>Members</Text>
                        <Text style={styles.metaValue}>{group.members?.filter(m => m.status === 'active').length ?? 0}</Text>
                    </View>
                    <View style={styles.metaRow}>
                        <Text style={styles.metaLabel}>Chargers</Text>
                        <Text style={styles.metaValue}>
                            {group.scope === 'all_host_chargers' ? 'All' : (group.chargers?.length ?? 0)}
                        </Text>
                    </View>
                    <View style={styles.metaRow}>
                        <Text style={styles.metaLabel}>Created</Text>
                        <Text style={styles.metaValue}>{new Date(group.created_at).toLocaleDateString()}</Text>
                    </View>
                    <View style={styles.metaRow}>
                        <Text style={styles.metaLabel}>Updated</Text>
                        <Text style={styles.metaValue}>{group.updated_at ? new Date(group.updated_at).toLocaleDateString() : '—'}</Text>
                    </View>
                </View>
            </View>
        );
    };

    const formatMemberDate = (dateStr: string): string => {
        const d = new Date(dateStr);
        return d.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
        });
    };

    const renderMemberRow = (member: PricingGroupMember) => {
        const isActive = member.status === 'active';
        const isProcessing = memberActionId === member.id;

        return (
            <View key={member.id} style={styles.memberRow}>
                <View style={styles.memberRowInfo}>
                    <View style={styles.memberAvatarContainer}>
                        <Text style={styles.memberAvatarText}>
                            {(member.user_email?.[0] ?? '?').toUpperCase()}
                        </Text>
                    </View>
                    <View style={styles.memberRowDetails}>
                        <Text style={styles.memberRowEmail} numberOfLines={1}>
                            {member.user_email}
                        </Text>
                        <View style={styles.memberRowMeta}>
                            <View
                                style={[
                                    styles.memberStatusBadge,
                                    { backgroundColor: isActive ? '#10B98133' : '#EF444433' },
                                ]}
                            >
                                <View
                                    style={[
                                        styles.memberStatusDot,
                                        { backgroundColor: isActive ? '#10B981' : '#EF4444' },
                                    ]}
                                />
                                <Text
                                    style={[
                                        styles.memberStatusText,
                                        { color: isActive ? '#10B981' : '#EF4444' },
                                    ]}
                                >
                                    {isActive ? 'Active' : 'Revoked'}
                                </Text>
                            </View>
                            <Text style={styles.memberRowDate}>
                                {formatMemberDate(member.created_at)}
                            </Text>
                        </View>
                    </View>
                </View>

                <View style={styles.memberRowActions}>
                    {isProcessing ? (
                        <ActivityIndicator size="small" color="#40BFB4" />
                    ) : isActive ? (
                        <Pressable
                            onPress={() => handleRevokeMember(member)}
                            style={({ pressed }) => [
                                styles.memberRowActionBtn,
                                styles.revokeBtn,
                                { opacity: pressed ? 0.6 : 1 },
                            ]}
                        >
                            <MaterialIcons name="block" size={14} color="#EF4444" />
                            <Text style={[styles.memberRowActionText, { color: '#EF4444' }]}>
                                Revoke
                            </Text>
                        </Pressable>
                    ) : (
                        <Pressable
                            onPress={() => handleRestoreMember(member)}
                            style={({ pressed }) => [
                                styles.memberRowActionBtn,
                                styles.restoreBtn,
                                { opacity: pressed ? 0.6 : 1 },
                            ]}
                        >
                            <MaterialIcons name="restore" size={14} color="#10B981" />
                            <Text style={[styles.memberRowActionText, { color: '#10B981' }]}>
                                Restore
                            </Text>
                        </Pressable>
                    )}
                </View>
            </View>
        );
    };

    const renderMembersTab = () => {
        if (!group) return null;
        const allMembers = group.members ?? [];
        const activeCount = allMembers.filter(m => m.status === 'active').length;
        const revokedCount = allMembers.length - activeCount;
        const members = showRevoked ? allMembers : allMembers.filter(m => m.status === 'active');

        return (
            <View style={styles.tabContent}>
                {/* Add Member */}
                <View style={styles.card}>
                    <Text style={styles.cardLabel}>Add Member</Text>
                    <View style={styles.addMemberRow}>
                        <TextInput
                            style={styles.emailInput}
                            value={newMemberEmail}
                            onChangeText={setNewMemberEmail}
                            placeholder="member@example.com"
                            placeholderTextColor="#8D8E90"
                            keyboardType="email-address"
                            autoCapitalize="none"
                            autoCorrect={false}
                            editable={!addingMember}
                        />
                        <LoadingButton
                            onPress={handleAddMember}
                            backgroundColor="#40BFB4"
                            isLoading={addingMember}
                            disabled={!newMemberEmail.trim()}
                            style={styles.addBtn}
                            textStyle={styles.addBtnText}
                        >
                            Add
                        </LoadingButton>
                    </View>
                </View>

                {/* Stats row */}
                <View style={styles.statsRow}>
                    <View style={styles.statCard}>
                        <Text style={styles.statValue}>{allMembers.length}</Text>
                        <Text style={styles.statLabel}>Total</Text>
                    </View>
                    <View style={styles.statCard}>
                        <Text style={[styles.statValue, { color: '#10B981' }]}>
                            {activeCount}
                        </Text>
                        <Text style={styles.statLabel}>Active</Text>
                    </View>
                    <View style={styles.statCard}>
                        <Text style={[styles.statValue, { color: '#EF4444' }]}>
                            {revokedCount}
                        </Text>
                        <Text style={styles.statLabel}>Revoked</Text>
                    </View>
                </View>

                {revokedCount > 0 && (
                    <View style={styles.revokedToggleRow}>
                        <Text style={styles.revokedToggleLabel}>
                            Show revoked ({revokedCount})
                        </Text>
                        <Switch
                            value={showRevoked}
                            onValueChange={setShowRevoked}
                            trackColor={{ false: '#3A3E44', true: 'rgba(64, 191, 180, 0.4)' }}
                            thumbColor={showRevoked ? '#40BFB4' : '#8D8E90'}
                        />
                    </View>
                )}

                {/* Member List */}
                {members.length === 0 ? (
                    <View style={styles.emptyState}>
                        <Ionicons name="people-outline" size={40} color="#8D8E90" />
                        <Text style={styles.emptyText}>
                            {revokedCount > 0 ? 'No active members' : 'No members yet'}
                        </Text>
                    </View>
                ) : (
                    <View style={styles.memberList}>
                        {members.map(member => renderMemberRow(member))}
                    </View>
                )}
            </View>
        );
    };

    const renderChargersTab = () => {
        if (!group) return null;

        if (group.scope === 'all_host_chargers') {
            return (
                <View style={styles.tabContent}>
                    <View style={styles.card}>
                        <View style={styles.allChargersInfo}>
                            <Ionicons name="globe-outline" size={32} color="#40BFB4" />
                            <Text style={styles.allChargersTitle}>All Host Chargers</Text>
                            <Text style={styles.allChargersDesc}>
                                This pricing group applies to every charger you own. Any new chargers you register will automatically be included.
                            </Text>
                        </View>
                    </View>
                    {group.chargers && group.chargers.length > 0 && (
                        <View style={styles.card}>
                            <Text style={styles.cardLabel}>Currently Included ({group.chargers.length})</Text>
                            {group.chargers.map(charger => (
                                <View key={charger.charger_id} style={styles.chargerRow}>
                                    <Ionicons name="flash-outline" size={18} color="#40BFB4" />
                                    <View style={styles.chargerInfo}>
                                        <Text style={styles.chargerName}>{charger.charger_name ?? 'Charger'}</Text>
                                        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4, marginTop: 2 }}>
                                            <View style={{ width: 6, height: 6, borderRadius: 3, backgroundColor: charger.is_enabled ? '#10B981' : '#EF4444' }} />
                                            <Text style={[styles.chargerSerial, { color: charger.is_enabled ? '#10B981' : '#EF4444' }]}>
                                                {charger.is_enabled ? 'Enabled' : 'Disabled'}
                                            </Text>
                                        </View>
                                    </View>
                                </View>
                            ))}
                        </View>
                    )}
                </View>
            );
        }

        // specific_chargers scope
        return (
            <View style={styles.tabContent}>
                <View style={styles.card}>
                    <Text style={styles.cardLabel}>Select Chargers</Text>
                    <Text style={styles.chargerSubLabel}>
                        Choose which chargers this pricing group applies to.
                    </Text>
                    {myChargers.length === 0 ? (
                        <View style={styles.emptyState}>
                            <Ionicons name="flash-off-outline" size={40} color="#8D8E90" />
                            <Text style={styles.emptyText}>No chargers registered</Text>
                        </View>
                    ) : (
                        myChargers.map(charger => {
                            const id = charger.uuid ?? '';
                            const isSelected = selectedChargerIds.includes(id);
                            return (
                                <Pressable
                                    key={id}
                                    onPress={() => toggleChargerSelection(id)}
                                    style={({ pressed }) => [
                                        styles.chargerCheckRow,
                                        isSelected && styles.chargerCheckRowSelected,
                                        { opacity: pressed ? 0.7 : 1 },
                                    ]}
                                >
                                    <View style={[styles.checkbox, isSelected && styles.checkboxSelected]}>
                                        {isSelected && <MaterialIcons name="check" size={16} color="#FFFFFF" />}
                                    </View>
                                    <View style={styles.chargerInfo}>
                                        <Text style={styles.chargerName}>{charger.name || charger.unit_serial}</Text>
                                        <Text style={styles.chargerSerial}>{charger.unit_serial}</Text>
                                    </View>
                                </Pressable>
                            );
                        })
                    )}
                </View>
                {chargersDirty && (
                    <LoadingButton
                        onPress={handleSaveChargers}
                        backgroundColor="#40BFB4"
                        isLoading={savingChargers}
                        style={styles.saveChargersBtn}
                        textStyle={styles.saveChargersBtnText}
                    >
                        Save Charger Assignments
                    </LoadingButton>
                )}
            </View>
        );
    };

    // ─── Main ───────────────────────────────────────────────

    if (loading) {
        return (
            <SafeAreaView edges={['top']} style={styles.safeArea}>
                <Header headerText="Pricing Group" />
                <View style={styles.loadingContainer}>
                    <ActivityIndicator size="large" color="#40BFB4" />
                </View>
            </SafeAreaView>
        );
    }

    return (
        <SafeAreaView edges={['top']} style={styles.safeArea}>
            <Header headerText={group?.name ?? 'Pricing Group'} />
            {renderTabs()}
            <ScrollView
                style={styles.scrollView}
                contentContainerStyle={styles.scrollContent}
                refreshControl={
                    <RefreshControl
                        refreshing={refreshing}
                        onRefresh={onRefresh}
                        tintColor="#40BFB4"
                        colors={['#40BFB4']}
                    />
                }
                keyboardShouldPersistTaps="handled"
            >
                {activeTab === 'details' && renderDetailsTab()}
                {activeTab === 'members' && renderMembersTab()}
                {activeTab === 'chargers' && renderChargersTab()}
            </ScrollView>
        </SafeAreaView>
    );
};

export default PricingGroupDetail;

const styles = StyleSheet.create({
    safeArea: {
        flex: 1,
        backgroundColor: '#1A1D21',
    },
    loadingContainer: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
    },
    scrollView: {
        flex: 1,
    },
    scrollContent: {
        paddingBottom: 40,
    },

    // Tabs
    tabRow: {
        flexDirection: 'row',
        paddingHorizontal: 16,
        borderBottomWidth: 1,
        borderBottomColor: '#292D33',
    },
    tab: {
        flex: 1,
        paddingVertical: 12,
        alignItems: 'center',
        borderBottomWidth: 2,
        borderBottomColor: 'transparent',
    },
    tabActive: {
        borderBottomColor: '#40BFB4',
    },
    tabText: {
        fontSize: 14,
        fontFamily: 'PlusJakartaSans-Regular',
        color: '#8D8E90',
    },
    tabTextActive: {
        color: '#40BFB4',
        fontWeight: '600',
    },

    // Tab content
    tabContent: {
        paddingHorizontal: 16,
        paddingTop: 16,
    },

    // Cards
    card: {
        backgroundColor: '#292D33',
        borderRadius: 12,
        padding: 16,
        marginBottom: 12,
    },
    cardLabel: {
        fontSize: 12,
        fontFamily: 'PlusJakartaSans-Regular',
        color: '#8D8E90',
        textTransform: 'uppercase',
        letterSpacing: 0.5,
        marginBottom: 10,
    },

    // Name editing
    nameRow: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    nameText: {
        fontSize: 18,
        fontFamily: 'PlusJakartaSans-Regular',
        color: '#FFFFFF',
        flex: 1,
    },
    inlineEditRow: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    inlineInput: {
        flex: 1,
        fontSize: 18,
        fontFamily: 'PlusJakartaSans-Regular',
        color: '#FFFFFF',
        borderBottomWidth: 1,
        borderBottomColor: '#40BFB4',
        paddingVertical: Platform.OS === 'ios' ? 6 : 4,
    },
    inlineBtn: {
        padding: 8,
        marginLeft: 4,
    },

    // Discount
    discountRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
    },
    discountBadge: {
        paddingHorizontal: 10,
        paddingVertical: 4,
        borderRadius: 6,
    },
    discountBadgeText: {
        fontSize: 13,
        fontWeight: '700',
        color: '#FFFFFF',
    },
    discountTypeText: {
        fontSize: 14,
        fontFamily: 'PlusJakartaSans-Regular',
        color: '#8D8E90',
    },
    percentEditRow: {
        flexDirection: 'row',
        alignItems: 'center',
        marginTop: 12,
    },
    percentInputWrap: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: '#1A1D21',
        borderRadius: 10,
        paddingHorizontal: 14,
        paddingVertical: Platform.OS === 'ios' ? 10 : 8,
    },
    percentInput: {
        fontSize: 20,
        fontWeight: '700',
        color: '#FFFFFF',
        minWidth: 48,
        textAlign: 'center',
        padding: 0,
    },
    percentSign: {
        fontSize: 18,
        fontWeight: '600',
        color: '#8D8E90',
        marginLeft: 2,
    },
    percentTapRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        marginTop: 10,
    },
    percentTapLabel: {
        fontSize: 13,
        fontFamily: 'PlusJakartaSans-Regular',
        color: '#8D8E90',
    },

    // Scope
    scopeRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
    },
    scopeText: {
        fontSize: 15,
        fontFamily: 'PlusJakartaSans-Regular',
        color: '#FFFFFF',
    },

    // Status
    statusRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        marginBottom: 14,
    },
    statusDot: {
        width: 10,
        height: 10,
        borderRadius: 5,
    },
    statusText: {
        fontSize: 15,
        fontFamily: 'PlusJakartaSans-Regular',
        color: '#FFFFFF',
    },
    toggleBtn: {
        marginTop: 4,
        borderRadius: 10,
    },
    toggleBtnText: {
        fontSize: 15,
        fontWeight: '600',
    },

    // Meta
    metaRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        paddingVertical: 6,
        borderBottomWidth: StyleSheet.hairlineWidth,
        borderBottomColor: '#3A3E44',
    },
    metaLabel: {
        fontSize: 14,
        fontFamily: 'PlusJakartaSans-Regular',
        color: '#8D8E90',
    },
    metaValue: {
        fontSize: 14,
        fontFamily: 'PlusJakartaSans-Regular',
        color: '#FFFFFF',
    },

    // Members - add
    addMemberRow: {
        flexDirection: 'row',
        alignItems: 'stretch',
        gap: 8,
    },
    emailInput: {
        flex: 1,
        backgroundColor: '#1A1D21',
        borderRadius: 10,
        paddingHorizontal: 14,
        paddingVertical: Platform.OS === 'ios' ? 12 : 10,
        fontSize: 15,
        fontFamily: 'PlusJakartaSans-Regular',
        color: '#FFFFFF',
    },
    addBtn: {
        borderRadius: 10,
        paddingVertical: 0,
        paddingHorizontal: 16,
        justifyContent: 'center',
    },
    addBtnText: {
        fontSize: 14,
        fontWeight: '600',
    },

    // Members - stats
    statsRow: {
        flexDirection: 'row',
        gap: 10,
        marginBottom: 16,
    },
    statCard: {
        flex: 1,
        backgroundColor: '#292D33',
        borderRadius: 12,
        padding: 14,
        alignItems: 'center',
    },
    statValue: {
        color: '#FFFFFF',
        fontSize: 22,
        fontWeight: '700',
        fontFamily: 'PlusJakartaSans-Regular',
    },
    statLabel: {
        color: '#8D8E90',
        fontSize: 12,
        fontFamily: 'PlusJakartaSans-Regular',
        marginTop: 2,
    },

    // Members - revoked toggle
    revokedToggleRow: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingHorizontal: 4,
        marginBottom: 12,
    },
    revokedToggleLabel: {
        fontSize: 14,
        fontFamily: 'PlusJakartaSans-Regular',
        color: '#8D8E90',
    },

    // Members - list
    memberList: {
        gap: 8,
    },
    memberRow: {
        backgroundColor: '#292D33',
        borderRadius: 14,
        padding: 14,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    memberRowInfo: {
        flexDirection: 'row',
        alignItems: 'center',
        flex: 1,
        marginRight: 10,
    },
    memberAvatarContainer: {
        width: 40,
        height: 40,
        borderRadius: 20,
        backgroundColor: '#3A3F47',
        alignItems: 'center',
        justifyContent: 'center',
        marginRight: 12,
    },
    memberAvatarText: {
        color: '#FFFFFF',
        fontSize: 16,
        fontWeight: '700',
    },
    memberRowDetails: {
        flex: 1,
    },
    memberRowEmail: {
        color: '#FFFFFF',
        fontSize: 14,
        fontWeight: '600',
        fontFamily: 'PlusJakartaSans-Regular',
    },
    memberRowMeta: {
        flexDirection: 'row',
        alignItems: 'center',
        marginTop: 4,
        gap: 8,
    },
    memberStatusBadge: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingHorizontal: 10,
        paddingVertical: 4,
        borderRadius: 20,
        gap: 5,
    },
    memberStatusDot: {
        width: 7,
        height: 7,
        borderRadius: 3.5,
    },
    memberStatusText: {
        fontSize: 12,
        fontWeight: '600',
        fontFamily: 'PlusJakartaSans-Regular',
    },
    memberRowDate: {
        color: '#8D8E90',
        fontSize: 11,
        fontFamily: 'PlusJakartaSans-Regular',
    },
    memberRowActions: {
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: 80,
    },
    memberRowActionBtn: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingHorizontal: 12,
        paddingVertical: 7,
        borderRadius: 20,
        gap: 5,
    },
    revokeBtn: {
        backgroundColor: '#EF444418',
    },
    restoreBtn: {
        backgroundColor: '#10B98118',
    },
    memberRowActionText: {
        fontSize: 12,
        fontWeight: '600',
        fontFamily: 'PlusJakartaSans-Regular',
    },

    // Chargers - all
    allChargersInfo: {
        alignItems: 'center',
        paddingVertical: 8,
        gap: 8,
    },
    allChargersTitle: {
        fontSize: 17,
        fontFamily: 'PlusJakartaSans-Regular',
        fontWeight: '600',
        color: '#FFFFFF',
    },
    allChargersDesc: {
        fontSize: 14,
        fontFamily: 'PlusJakartaSans-Regular',
        color: '#8D8E90',
        textAlign: 'center',
        lineHeight: 20,
    },
    chargerRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
        paddingVertical: 10,
        borderBottomWidth: StyleSheet.hairlineWidth,
        borderBottomColor: '#3A3E44',
    },
    chargerInfo: {
        flex: 1,
    },
    chargerName: {
        fontSize: 15,
        fontFamily: 'PlusJakartaSans-Regular',
        color: '#FFFFFF',
    },
    chargerSerial: {
        fontSize: 12,
        fontFamily: 'PlusJakartaSans-Regular',
        color: '#8D8E90',
        marginTop: 2,
    },

    // Chargers - specific (checklist)
    chargerSubLabel: {
        fontSize: 13,
        fontFamily: 'PlusJakartaSans-Regular',
        color: '#8D8E90',
        marginBottom: 12,
    },
    chargerCheckRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
        paddingVertical: 12,
        borderBottomWidth: StyleSheet.hairlineWidth,
        borderBottomColor: '#3A3E44',
    },
    chargerCheckRowSelected: {
        backgroundColor: 'rgba(64, 191, 180, 0.06)',
    },
    checkbox: {
        width: 24,
        height: 24,
        borderRadius: 6,
        borderWidth: 2,
        borderColor: '#8D8E90',
        alignItems: 'center',
        justifyContent: 'center',
    },
    checkboxSelected: {
        backgroundColor: '#40BFB4',
        borderColor: '#40BFB4',
    },
    saveChargersBtn: {
        marginTop: 4,
        marginHorizontal: 16,
        borderRadius: 12,
    },
    saveChargersBtnText: {
        fontSize: 15,
        fontWeight: '600',
    },

    // Empty state
    emptyState: {
        alignItems: 'center',
        paddingVertical: 24,
        gap: 8,
    },
    emptyText: {
        fontSize: 14,
        fontFamily: 'PlusJakartaSans-Regular',
        color: '#8D8E90',
    },
});
